import axios from '../plugins/axios';

export const OPERATOR_TOKEN_KEY = 'posOperatorToken';
export const OPERATOR_DATA_KEY = 'posOperator';
export const OPERATOR_PERMISSIONS_KEY = 'posOperatorPermissions';
export const OPERATOR_SESSION_CACHE_MS = 15000;

let lastValidatedAt = 0;
let validationPromise = null;

const parseStoredJson = (key, fallback) => {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || '');
    return value == null ? fallback : value;
  } catch {
    return fallback;
  }
};

export const listOperators = async () => {
  const response = await axios.get('/api/tenant/pos/operators');
  return Array.isArray(response.data?.operators) ? response.data.operators : [];
};

export const loginOperator = async (operatorId, password = '') => {
  const response = await axios.post('/api/tenant/pos/operator-login', {
    operatorId,
    password,
  });
  const { accessToken, operator, permissions } = response.data || {};
  if (!accessToken || !operator?.id || !Array.isArray(permissions)) {
    throw new Error('El servidor devolvió una sesión de usuario incompleta.');
  }

  sessionStorage.setItem(OPERATOR_TOKEN_KEY, accessToken);
  sessionStorage.setItem(OPERATOR_DATA_KEY, JSON.stringify(operator));
  sessionStorage.setItem(OPERATOR_PERMISSIONS_KEY, JSON.stringify(permissions));
  lastValidatedAt = Date.now();
  return { accessToken, operator, permissions };
};

/**
 * Revalida la sesión con caché breve y comparte la petición concurrente.
 * @param {{ force?: boolean, maxAgeMs?: number }} options
 */
export const validateOperatorSession = async ({
  force = false,
  maxAgeMs = OPERATOR_SESSION_CACHE_MS,
} = {}) => {
  if (!getOperatorToken()) return null;
  const cachedOperator = getActiveOperator();
  const cachedPermissions = getOperatorPermissions();
  if (
    !force
    && cachedOperator?.id
    && Array.isArray(cachedPermissions)
    && Date.now() - lastValidatedAt < maxAgeMs
  ) {
    return { operator: cachedOperator, permissions: cachedPermissions };
  }

  if (!validationPromise) {
    validationPromise = axios.get('/api/tenant/pos/session')
      .then((response) => {
        const { operator, permissions } = response.data || {};
        if (!operator?.id || !Array.isArray(permissions)) {
          clearOperatorSession();
          return null;
        }
        sessionStorage.setItem(OPERATOR_DATA_KEY, JSON.stringify(operator));
        sessionStorage.setItem(OPERATOR_PERMISSIONS_KEY, JSON.stringify(permissions));
        lastValidatedAt = Date.now();
        return { operator, permissions };
      })
      .finally(() => {
        validationPromise = null;
      });
  }
  return validationPromise;
};

export const getOperatorToken = () => sessionStorage.getItem(OPERATOR_TOKEN_KEY);

export const getActiveOperator = () => parseStoredJson(OPERATOR_DATA_KEY, null);

export const getOperatorPermissions = () => {
  const permissions = parseStoredJson(OPERATOR_PERMISSIONS_KEY, []);
  return Array.isArray(permissions) ? permissions : [];
};

export const hasOperatorPermission = (permission) => (
  !permission || getOperatorPermissions().includes(permission)
);

export const clearOperatorSession = () => {
  lastValidatedAt = 0;
  validationPromise = null;
  sessionStorage.removeItem(OPERATOR_TOKEN_KEY);
  sessionStorage.removeItem(OPERATOR_DATA_KEY);
  sessionStorage.removeItem(OPERATOR_PERMISSIONS_KEY);
};
